<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="listings">
					<app-heading :title="`${market.name} Listings`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('marketmath.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								:to="{ name: 'market-listing', params: { listing: 'new' } }"
							>Add Listing</app-button>
						</template>
						<template v-slot:right>
							<input-file
								type="csv"
								button
								small
								outlined
								label="Import (CSV)"
								@load="beforeImport"
							/>
							<app-button
								button
								small
								outlined
								color="secondary"
								icon="fa-download"
								@click="exportV2Listings"
							>Export (V2)</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<input-button-group
								color="primary"
								mandatory
								:value="0"
								@change="onStatusChange"
							>
								<app-button no-margin small>Active</app-button>
								<app-button no-margin small>Pending</app-button>
							</input-button-group>
						</app-cell>
					</app-row>
					<app-row>
						<app-cell>
							<app-data-table
								:items="listings"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:initial-state="tableState"
								search
								@search="onSearch"
								@update="fetchListings"
								sort-by="date"
								sort-desc
							>
								<template v-slot:validated="{ item }">
									<span v-if="item.sent">
										<i class="success--text">Validated &amp; Sent</i>
									</span>
									<span v-else-if="item.validated">
										<i class="success--text">Validated</i>
									</span>
									<span v-else-if="item.survey">
										<i class="warning--text">Validation In Progress</i>
									</span>
									<span v-else>
										<i class="error--text">Not Validated</i>
									</span>
								</template>
								<template v-slot:date="{ item }">
									<span v-if="item.date">
										{{ item.date | date(market.timezone) }}
									</span>
									<span v-else class="warning--text">
										<i>TBD</i>
									</span>
								</template>
								<template v-slot:title="{ item }">
									<div v-if="item.url">
										<a :href="item.url" target="_blank">{{ item.title }}</a>
									</div>
									<div v-else>{{ item.title }}</div>
									<div><i>{{ item.slug }}</i></div>
								</template>
								<template v-slot:revenue="{ item }">
										<div v-if="item.revenue">{{ item.revenue | currency }}</div>
										<div v-else class="warning--text"><i>No Revenue</i></div>
										<div v-if="item.score">{{ item.score.toFixed(1) }}</div>
										<div v-else class="warning--text"><i>No Score</i></div>
								</template>
								<template v-slot:address="{ item }">
									<div>{{ item.address }}</div>
									<div v-if="item.complex"><i>{{ item.complex.name }}</i></div>
									<div v-else class="warning--text"><i>Missing Complex</i></div>
								</template>
								<template v-slot:realtor="{ item }">
									<span
										v-if="item.realtor && item.realtor.email && item.realtor.email !== ''"
									>
										<router-link
											:to="{ name: 'market-realtor', params: { realtor: item.realtor._id }}"
											target="_blank"
										>
											{{ item.realtor.name }}
										</router-link>
									</span>
									<span
										v-else-if="item.realtor"
									>
										<app-tooltip text="Missing email">
											<router-link
												:to="{ name: 'market-realtor', params: { realtor: item.realtor._id }}"
												class="warning--text"
												target="_blank"
											>
												{{ item.realtor.name }}
											</router-link>
										</app-tooltip>
									</span>
									<span v-else class="error--text">
										<app-tooltip text="Realtor not linked">
											{{ item.realtor_name }}
										</app-tooltip>
									</span>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										color="secondary"
										:to="{ name: 'market-listing-analysis', params: { listing: item._id } }"
										icon="fa-chart-line"
										table
										:disabled="!item.analysis_preset || !item.validated
											|| !item.revenue || !item.score"
									>
										Analysis
									</app-button>
									<app-button
										color="secondary"
										icon="fa-paper-plane"
										table
										:disabled="!item.analysis_preset || !item.validated
											|| !item.revenue || !item.score || !item.realtor || !item.complex"
										@click="onSend(item)"
									>
										Send
									</app-button>
									<app-button
										v-if="$ravello.can('marketmath.put')"
										color="secondary"
										:to="{ name: 'market-listing', params: { listing: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('marketmath.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove listing <b>${item.name}</b>?`"
										title="Remove Listing"
										:disabled="item.survey && !item.validated"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'market-listing', params: { listing: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
		<app-dialog v-model="importDialog" persistent max-width="500" title="Listings City">
			<app-dialog-body>
				<div>In which city to you which to import your listings?</div>
				<input-select
					v-model="importCity"
					:items="cities"
					label="City"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					flat="flat"
					@click="importLegacy ? onImportLegacy($event) : onImport($event)"
					:disabled="!importCity"
				>
					Import
				</app-button>
				<app-button
					flat="flat"
					@click="closeImport"
				>
					Cancel
				</app-button>
			</app-dialog-footer>
		</app-dialog>
		<app-dialog v-model="sendDialog" persistent max-width="500" title="Send listing analysis">
			<app-dialog-body>
				<div v-if="!sendSent">Are you sure you want to send the survey analysis?</div>
				<div v-else>
					<div>Are you sure you want to send the survey analysis again?</div>
					<div>Last sent on <b>{{ sendSentOn | datetime(market.timezone) }}</b></div>
				</div>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="secondary"
					flat="flat"
					@click="onSendTest()"
				>
					Send test
				</app-button>
				<app-button
					:color="sendSent ? 'warning' : 'primary'"
					flat="flat"
					@click="sendAnalysis"
				>
					{{ sendSent ? 'Send Again' : 'Send' }}
				</app-button>
				<app-button
					flat="flat"
					@click="closeSend"
				>
					Cancel
				</app-button>
			</app-dialog-footer>
		</app-dialog>
		<app-dialog v-model="sendTestDialog" persistent max-width="500" title="Send test">
			<app-dialog-body>
				<div>Please validate or enter a different email address to receive the test</div>
				<input-text
					v-model="sendTestEmail"
					type="email"
					label="Email"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="secondary"
					flat="flat"
					@click="sendAnalysis(true)"
				>
					Send test
				</app-button>
				<app-button
					flat="flat"
					@click="closeSend"
				>
					Cancel
				</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import moment from 'moment'

import { filters, search, tableState } from '@/mixins'
import { marketMathTabs } from '@/datasources/tabs'
import { importCollection2, exportCollection2 } from '@/utils/import'

const IMPORT_FIELDS = ['title', 'mls', 'address', 'listing_time', 'bedrooms', 'url', 'featured_image', 'price',
	'property_type', 'description', 'realtor_name', 'brokerage']
export default {
	mixins: [filters, search, tableState],
	data() {
		return {
			market: {
				name: '',
				timezone: 'UTC',
			},
			listings: [],
			importData: [],
			importDialog: false,
			importCity: null,
			importLegacy: false,
			sendDialog: false,
			sendSent: false,
			sendTestDialog: false,
			sendTestEmail: '',
			sendListing: null,
			sendSentOn: null,
			count: -1,
			refresh: false,
			tabs: [],
			cities: [],
			headers: [{
				text: 'Status',
				value: 'validated',
				sortable: true,
			}, {
				text: 'Published On',
				value: 'date',
				sortable: true,
			}, {
				text: 'MLS',
				value: 'mls',
				sortable: true,
			}, {
				text: 'Title',
				value: 'title',
				sortable: true,
			}, {
				text: 'Address/Complex',
				value: 'address',
				sortable: true,
			}, {
				text: 'Revenue/Score',
				value: 'revenue',
				sortable: true,
			}, {
				text: 'Agent',
				value: 'realtor',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				width: '25%',
				sortable: false,
			}],
			defaultTableSort: '-date',
			filters: {
				status: '',
			},
			statuses: [{
				text: 'Not Validated',
				value: 1,
			}, {
				text: 'Validation In Progress',
				value: 2,
			}, {
				text: 'Validated',
				value: 3,
			}, {
				text: 'Validated & Sent',
				value: 4,
			}],
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
	},
	async mounted() {
		this.market = await this.$ravello.graphql('market', this.marketId)
			.fields(['name, timezone'])
			.query()

		this.headers.formatArgs = this.market.timezone
		this.cities = await this.$ravello.graphql('cities')
			.parent(this.$ravello.graphql('market', this.marketId))
			.datasource()
			.sort('name')
			.query()

		this.tabs = marketMathTabs(this.marketId)
	},
	methods: {
		async fetchListings(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('listings')
				.parent(this.$ravello.graphql('market', this.marketId))
				.include(this.$ravello.graphql('realtor').fields(['name', 'email', '_id']))
				.include(this.$ravello.graphql('market_city').fields(['name']))
				.include(this.$ravello.graphql('complex').fields(['name']))
				.include(this.$ravello.graphql('survey').fields(['name']))
				.fields([
					'_id',
					'date',
					'title',
					'mls',
					'slug',
					'address',
					'url',
					'realtor_name',
					'listing_time',
					'analysis_preset',
					'validated',
					'validated_on',
					'sent',
					'sent_on',
					'revenue',
					'score',
				])
				.fromPagination(pagination)

			if (this.count === -1 && !this.searchString) {
				request.count('count')
			}

			if (this.searchText) {
				request.search(this.searchText)
			}
			try {
				this.listings = await request.query()

				if (this.searchText) {
					this.count = this.listings.length
				}
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('listings')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		beforeImport(importData) {
			this.importData = importData
			this.importDialog = true
		},

		beforeLegacyImport(importData) {
			this.importData = importData
			this.importLegacy = true
			this.importDialog = true
		},

		closeImport() {
			this.importData = []
			this.importDialog = false
			this.importLegacy = false
			this.importCity = null
		},

		onSend({
			_id,
			sent,
			sent_on: sentOn,
		}) {
			this.sendDialog = true
			this.sendSent = sent
			this.sendListing = _id
			this.sendSentOn = sentOn
		},

		onSendTest() {
			this.sendDialog = false
			this.sendTestDialog = true
			this.sendTestEmail = this.$ravello.me.email
		},

		onStatusChange(value) {
			switch (value) {
			case 1:
				this.$router.push({ name: 'market-listings-pending' })
				break
			default:
			}
		},

		closeSend() {
			this.sendDialog = false
			this.sendTestDialog = false
			this.sendSent = false
			this.sendListing = null
			this.sendSentOn = null
			this.sendTestEmail = ''
		},

		async onImportLegacy() {
			this.$store.dispatch('app/startLoading')

			const [complexes, sponsors, presets] = await Promise.all([
				this.$ravello.graphql('complexes')
					.parent(this.$ravello.graphql('market', this.marketId))
					.datasource('slug')
					.query(),
				this.$ravello.graphql('marketsponsors')
					.field('_id')
					.limit(1)
					.query(),
				this.$ravello.graphql('analysispresets')
					.parent(this.$ravello.graphql('market', this.marketId))
					.field('_id')
					.limit(1)
					.query(),
			])


			const complexesMap = complexes.reduce((acc, complex) => ({
				...acc,
				[complex.text]: complex.value,
			}), {})

			let fakeMls = 99999000
			const validImportData = this.importData
				.map((data) => {
					const importData = IMPORT_FIELDS.reduce((acc, field) => ({
						...acc,
						...(typeof data[field] !== 'undefined' ? { [field]: data[field] } : {}),
						market_city: this.importCity,
						sponsor: sponsors[0] ? sponsors[0]._id : null,
						analysis_preset: presets[0] ? presets[0]._id : null,
					}), {})

					importData.mls = fakeMls++
					importData.featured_image = 'LEGACYYY'

					if (data.validated) {
						importData.validated = true
						importData.validated_on = moment(data.validated.date).toDate()
						importData.survey_legacy = data.validated.participants
							.map(participant => participant.score)
						importData.score = importData.survey_legacy.length
							? parseFloat(((importData.survey_legacy.reduce((acc, score) => acc + score, 0))
								/ importData.survey_legacy.length).toFixed(1))
							: 0
					}

					if (data.complex_slug && complexesMap[data.complex_slug]) {
						importData.complex = complexesMap[data.complex_slug]
					}

					if (data.revenue) {
						importData.revenue = parseInt(data.revenue.replace(/,/g, ''), 10)
					}
					return importData
				})

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('listings')
			try {
				await importCollection2(validImportData, request, { upsert: true })
				this.refresh = 'hard'
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.closeImport()
			this.$store.dispatch('app/stopLoading')
		},

		async onImport() {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('listings')

			const validImportData = this.importData.filter(data => data.mls && data.mls !== '')
				.map(data => IMPORT_FIELDS.reduce((acc, field) => ({
					...acc,
					...(typeof data[field] !== 'undefined' ? { [field]: data[field] } : {}),
					market_city: this.importCity,
				}), {}))

			try {
				await importCollection2(validImportData, request, { upsert: true })
				this.refresh = 'hard'
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.closeImport()
			this.$store.dispatch('app/stopLoading')
		},

		async sendAnalysis(test = false) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('listings')
					.to(this.sendListing)
					.to('sendanalysis')
					.create(test ? {
						to: this.sendTestEmail,
					} : {})
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.closeSend()
			this.$store.dispatch('app/stopLoading')
		},

		async exportV2Listings() {
			const request = this.$ravello.graphql('listings')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'slug', 'validated', 'sent', 'mls', 'zpid', 'date', 'title', 'address', 'longitude',
					'latitude', 'bedrooms', 'bathrooms', 'url', 'featured_image', 'price', 'description', 'property_type',
					'brokerage', 'realtor_name', 'revenue', 'occupancy_rate'])
				.include(this.$ravello.graphql('complex').field('_id')
					.include(this.$ravello.graphql('area').field('_id')))
				.include(this.$ravello.graphql('realtor').field('_id'))

			await exportCollection2('listings', 'json', request, {
				graphql: true,
				map: entry => ({
					legacyId: entry._id,
					uriName: entry.slug,
					mls: entry.mls,
					externalId: entry.zpid,
					source: 'zillow',
					listedSince: entry.date,
					title: entry.title,
					address: entry.address,
					long: entry.longitude,
					lat: entry.latitude,
					bedrooms: entry.bedrooms,
					bathrooms: entry.bathrooms,
					url: entry.url,
					featuredImage: entry.featured_image,
					price: entry.price,
					description: entry.description,
					propertyType: entry.property_type,
					brokerageName: entry.brokerage,
					realtorName: entry.realtor_name,
					revenue: entry.revenue,
					occupancyRate: entry.occupancy_rate,
					adr: entry.revenue && entry.occupancy_rat
						? Math.round((entry.revenue / (365 * (entry.occupancy_rate / 100)))) : undefined,
					// eslint-disable-next-line no-nested-ternary
					status: (entry.sent ? 'sent' : (entry.validated ? 'validated' : 'declined')),
					legacyArea: entry.complex ? entry.complex.area._id : undefined,
					legacyComplex: entry.complex ? entry.complex._id : undefined,
					legacyRealtor: entry.realtor ? entry.realtor._id : undefined,
				}),
			})
		},
	},
}
</script>
